<template>
  <div class="child-route-wrapper">
    <h1 class="registration-form-title">
      Upload your company {{ type }}
    </h1>
    <DragAndDrop
      :select-error="selectError"
      :is-success="isSuccess"
      :type="type"
      :file="file"
      @select-image="handleSelectImage"
    />
    <UploadStatus
      v-if="isUploading || isSuccess || isUploadError"
      :file="file"
      :is-uploading="isUploading"
      :is-success="isSuccess"
      :is-delete-enabled="isDeleteEnabled"
      :is-error="isUploadError"
      :progress-percentage="progressPercentage"
      @remove-image="handleRemoveImage"
      @stop-upload="cancelUpload"
    />
    <b-button
      class="registration-button"
      @click="handleNext"
    >
      Next
    </b-button>
  </div>
</template>
<script>
import { BButton } from 'bootstrap-vue'
import store from '@/store'
import { RESET_IMAGE, SET_FORM_DATA, UPLOAD_IMAGE } from '@/store/modules/register.module'
import { AUTH_REGISTER_V2_CHILDREN } from '@/router/routes/utils'
import constants, { MAX_PROFILE_IMAGE_SIZE, PROFILE_IMAGE_TYPES } from '@/constants'
import axios from 'axios'
import { getFileSize, getProfileImageFileType } from '@/utils/fileUtils'
import analytics from '@/@core/utils/analytics'
import { apiToastWarning } from '@/@core/utils/toast'
import DragAndDrop from './DragAndDrop.vue'
import UploadStatus from './UploadStatus.vue'

export default {
  name: 'UploadImage',
  components: {
    BButton,
    DragAndDrop,
    UploadStatus,
  },
  props: {
    type: {
      type: String,
      default: 'Logo',
    },
  },
  data() {
    return {
      progressPercentage: 0,
      isUploading: false,
      isSuccess: false,
      selectError: '',
      isUploadError: false,
      interval: null,
      file: null,
      fileType: '',
      nextRoute: '',
      isDeleteEnabled: false,
      uploadCancelToken: null,
      fileUrl: '',
    }
  },
  watch: {
    progressPercentage(newVal) {
      if (newVal === 100) {
        clearInterval(this.interval)
        this.isUploading = false
        this.isSuccess = true
      }
    },
  },
  created() {
    this.fileType = getProfileImageFileType(this.type, 1)
    this.file = store.state.register[`local_${this.type}`] || null
    this.isDeleteEnabled = !!store.state.register[`local_${this.type}`]
    this.isSuccess = !!store.state.register[`local_${this.type}`]
    this.fileUrl = store.state.register.formData[`${this.type}Url`]
    if (this.type === 'logo') {
      this.nextRoute = AUTH_REGISTER_V2_CHILDREN.UPLOAD_BANNER
    } else {
      this.nextRoute = AUTH_REGISTER_V2_CHILDREN.SET_LAUNCH_DATE
    }
    analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.UPLOAD_COMPANY_IMAGE_PAGE_VIEW, {
      pageName: `Upload your company ${this.type}`,
    })
  },
  methods: {
    handleRemoveImage(type, file = null) {
      this.isUploading = false
      this.isSuccess = false
      this.selectError = ''
      this.isUploadError = false
      this.progressPercentage = 0
      this.trackRemoveImage(type, file || this.file)
      this.file = null
      this.fileUrl = ''
      store.commit(RESET_IMAGE, this.type)
    },
    resetSelectError() {
      setTimeout(() => {
        this.selectError = ''
      }, 3000)
    },
    validateFile(file) {
      const fileExtension = file.name.split('.').pop()
      if (!PROFILE_IMAGE_TYPES.includes(fileExtension)) {
        this.selectError = 'File type is not valid'
        this.isSuccess = false
        this.isUploading = false
        this.progressPercentage = 0
        this.file = null
        this.resetSelectError()
        return false
      }
      if (file.size > MAX_PROFILE_IMAGE_SIZE) {
        this.selectError = 'File size should be less than 10MB'
        this.isSuccess = false
        this.isUploading = false
        this.progressPercentage = 0
        this.file = null
        this.resetSelectError()
        return false
      }
      return true
    },
    async handleSelectImage(files) {
      const file = files[0]
      if (!this.validateFile(file)) return
      this.file = file
      this.isUploading = true
      this.isSuccess = false
      this.isUploadError = false
      this.progressPercentage = 0

      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }

      try {
        // Create a new cancel token source
        this.uploadCancelToken = axios.CancelToken.source()
        const response = await store.dispatch(UPLOAD_IMAGE, {
          file,
          type: this.type,
          fileType: this.fileType,
          onUploadProgress: progressEvent => {
            if (progressEvent.total) {
              this.progressPercentage = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              )
            }
          },
          cancelToken: this.uploadCancelToken.token,
        })
        if (response && response.data) {
          this.handleSuccess(response.data.data.location)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isUploading = false
      }
    },
    cancelUpload() {
      if (this.uploadCancelToken) {
        this.uploadCancelToken.cancel('Upload cancelled by user')
        this.uploadCancelToken = null
        this.handleRemoveImage('cancel', this.file)
      }
    },
    handleNext() {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.UPLOAD_COMPANY_IMAGE_CLICK_NEXT_BUTTON, {
        pageName: `Upload your company ${this.type}`,
        fileUrl: this.fileUrl,
      })
      if (!this.fileUrl) {
        apiToastWarning('Please upload an image')
        return
      }
      this.$router.push({ name: this.nextRoute })
    },
    handleSuccess(location) {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.UPLOAD_COMPANY_IMAGE_UPLOAD_SUCCESS, {
        pageName: `Upload your company ${this.type}`,
        fileUrl: location,
      })
      this.fileUrl = location
      this.isUploading = false
      this.isSuccess = true
      setTimeout(() => {
        this.isDeleteEnabled = true
      }, 750)
      store.commit(SET_FORM_DATA, { [`${this.type}Url`]: location })
    },
    handleError(error) {
      if (!axios.isCancel(error)) {
        this.isUploadError = true
        this.isSuccess = false
        this.isUploading = false
        this.progressPercentage = 0
        this.isDeleteEnabled = false
        analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.UPLOAD_COMPANY_IMAGE_UPLOAD_FAILED, {
          pageName: `Upload your company ${this.type}`,
          error: JSON.stringify(error),
        })
      }
    },
    trackRemoveImage(removeType, file) {
      let eventName = ''
      if (removeType === 'cancel') {
        eventName = constants.TRACKS.ACTIONS.REGISTER_V2.UPLOAD_COMPANY_IMAGE_CLICK_CANCEL_UPLOAD_BUTTON
      } else if (removeType === 'try-again') {
        eventName = constants.TRACKS.ACTIONS.REGISTER_V2.UPLOAD_COMPANY_IMAGE_CLICK_TRY_AGAIN
      } else if (removeType === 'delete') {
        eventName = constants.TRACKS.ACTIONS.REGISTER_V2.UPLOAD_COMPANY_IMAGE_CLICK_REMOVE_IMAGE_BUTTON
      }
      const eventPayload = {
        pageName: `Upload your company ${this.type}`,
        fileName: file.name,
        fileSize: getFileSize(file.size),
        fileType: file.type,
      }
      if (removeType === 'delete') {
        eventPayload.fileUrl = this.fileUrl
      }
      analytics.track(eventName, eventPayload)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../common-styles.scss';
</style>
